import { type FC, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { TriggerButton } from "widgets";

import type { PlaybookEvent, User } from "@/graphql.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { PlaybookEventEditDialog } from "@/ui-modules/admin/PlaybookEventEditDialog.tsx";

export interface IEditPlaybookEventButtonProps {
  eventId: number;
  onSaved?: () => void;
  showLabel?: boolean;
  eventOwner: Pick<PlaybookEvent["owner"], "username">;
}

export const EditPlaybookEventButton: FC<IEditPlaybookEventButtonProps> = (props) => {
  const user: User = useSelector<IAppState, User>((state) => state.auth.session, shallowEqual);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);

  const disabledTooltip = useMemo(() => {
    if (user.username !== props.eventOwner?.username) {
      return "You are not allowed to edit this event because you are not the owner";
    }
    return undefined;
  }, [props.eventOwner?.username, user.username]);

  return (
    <>
      <TriggerButton
        id={"edit-playbook-event-button"}
        icon={"edit"}
        onPress={() => setShowEditDialog(true)}
        tooltip={"Click to edit the name or description for this event."}
        isDisabled={disabledTooltip}
        label={"Edit"}
        showLabel={props.showLabel}
      />

      {showEditDialog && (
        <PlaybookEventEditDialog
          eventId={props.eventId}
          onSave={() => {
            setShowEditDialog(false);
            props.onSaved?.();
          }}
          onCancel={() => {
            setShowEditDialog(false);
          }}
        />
      )}
    </>
  );
};
